import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './ApplicableFees.scss';
import ApplicableFeeDetails from '../../stateless/ApplicableFeeDetails';
import useSiteApplicableFees from '../../../../modules/react/hooks/useSiteApplicableFees';
import { STOREDGE, SITELINK } from '../../../../constants/softwareProviders';
import useCurrentSite from '../../../../modules/react/hooks/useCurrentSite';

const RenderDescription = ({ feeList, isLoading }) => {
  let description;

  if (isLoading) {
    description = (
      <>
        <li className={styles.skeletonLine} />
        <li className={styles.skeletonLine} />
        <li className={styles.skeletonLine} />
      </>
    );
  } else if (!feeList || feeList.length === 0) {
    return <ApplicableFeeDetails />;
  } else {
    const one = '(one-time)';
    const monthly = '(/mo)';
    description = feeList.reduce((acc, fee) => {
      if (fee.total > 0) {
        const feeType = fee.kind === 'service' ? `$${fee.total.toFixed(2)} ${monthly}` : `$${fee.total.toFixed(2)} ${one}`;
        acc.push(
          <li key={fee.id}>
            {fee.description}: {feeType}
          </li>,
        );
      }
      return acc;
    }, []);
  }

  return (
    <div>
      <p className={styles.feeHead}>The following fees shall apply:</p>
      <ul className={styles.feeDetails}>{description}</ul>
    </div>
  );
};

const RenderHead = ({ totalFee }) => (
  <>{totalFee ? `+ $${totalFee.toFixed(2)} in fees` : '+ applicable fees'}</>
);

const MapHeader = ({ totalFee, handleToggle, loading, showDetails }) => (
  <button className={styles.feesLink} onClick={handleToggle} type="button">
    {loading ? '+ applicable fees' : <RenderHead totalFee={totalFee} />}
    <span
      className={cx(['icon', showDetails ? 'icon-filter-arrow-up' : 'icon-filter-arrow-down', styles.arrow])}
    />
  </button>
);

const getFees = (feeData) => {
  if (!feeData) { return null; }

  const { fees: feeArray, totalFee } = feeData;
  return feeArray?.length ? { totalFee, feeArray } : null;
};

const ApplicableFees = ({
  facility,
  feeFilter,
  isMap,
  isHead,
  handleToggle,
  showDetails,
  feesLinkClass,
  applicableFeesDownArrowClass,
  feesDescriptionContainerClass,
  feesLinkThemeClass,
  descriptionBoxClass,
}) => {
  // Adding Display criteria to show applicable fees
  const { showApplicableFees } = useCurrentSite();
  const [shouldDisplayApplicableFee, setShouldDisplayApplicableFee] = useState(showApplicableFees);
  const [applicableFeesSettings, setApplicableFeesSettings] = useState();

  const { loading, applicableFee, stateSettings } = useSiteApplicableFees();

  useEffect(() => {
    if (stateSettings) {
      const { applicableFeeSettings } = stateSettings;
      setApplicableFeesSettings(applicableFeeSettings);
    }
  }, [stateSettings]);

  const enabledStates = applicableFeesSettings?.states
    ? applicableFeesSettings.states.filter(state => state.enabled).map(state => state.state)
    : [];

  const isCaliforniaState = enabledStates.includes(facility?.address?.state);

  const applicableFeesEnabled = facility?.applicableFeesEnabled;

  useEffect(() => {
    setShouldDisplayApplicableFee(
      isCaliforniaState
      && (showApplicableFees ?? true)
      && (applicableFeesEnabled ?? true),
    );
  }, [showApplicableFees, isCaliforniaState, applicableFeesEnabled]);

  if (!shouldDisplayApplicableFee) {
    return null;
  }

  if (!feeFilter) {
    return null;
  }

  const { facilityId, unitGroupId, unitTierId, unitSummaryId } = feeFilter || {};

  const facilityFees = applicableFee?.[facilityId];
  const softwareProvider = facilityFees?.softwareProvider;
  let feeData = null;

  if (softwareProvider === STOREDGE) {
    if (unitSummaryId) {
      feeData = facilityFees?.feeSummary[unitSummaryId];
    } else if (unitTierId) {
      const key = `${unitGroupId}-${unitTierId}`;
      feeData = facilityFees?.allFees[key];
    } else if (unitGroupId) {
      feeData = facilityFees?.allFees[unitGroupId];
    } else if (facilityId) {
      feeData = facilityFees?.feeSummary.MINIMUM;
    }
  } else if (softwareProvider === SITELINK) {
    feeData = facilityFees?.allFees.FEES;
  }


  const { totalFee, feeArray } = getFees(feeData) || {};

  if (!loading && (!totalFee || totalFee === 0)) {
    return null;
  }
  const feeContent = (
    <RenderDescription feeList={feeArray} isLoading={loading} />
  );

  if (isMap) {
    if (isHead) {
      return (
        <MapHeader
          totalFee={totalFee}
          handleToggle={handleToggle}
          loading={loading}
          showDetails={showDetails}
        />
      );
    }
    return feeContent;
  }

  return (
    <div className={`${styles.feesDescriptionContainer} ${feesDescriptionContainerClass || ''}`}>
      <button
        className={`${feesLinkClass || styles.feesLink} ${feesLinkThemeClass}`}
        type="button"
      >
        <RenderHead totalFee={totalFee} />
        <span
          className={cx([
            'icon',
            'icon-filter-arrow-down',
            applicableFeesDownArrowClass || styles.downArrow,
          ])}
        />
      </button>
      <div className={`${styles.descriptionBox} ${descriptionBoxClass}`}>
        {feeContent}
      </div>
    </div>
  );
};

export default ApplicableFees;
